// Derives a uniform list of "change entries" from the policy-change form
// values, across all section shapes (record arrays + single objects), so the
// ChangesRequested summary can render them the same way regardless of section.

import { IHabitationalPolicyChange } from "../../../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { ChangeSectionKey } from "./WhatChangedBoard";

export interface IChangeFieldRow {
  label: string;
  from?: React.ReactNode;
  to?: React.ReactNode;
  // Whether this field is required but empty (drives "fields missing").
  missing?: boolean;
}

export interface IChangeEntry {
  // Unique id for keying / edit / remove.
  id: string;
  section: ChangeSectionKey;
  subType: string;
  mode: string; // ADD / CHANGE / DELETE
  title: string;
  fields: IChangeFieldRow[];
  missingCount: number;
  // For record-array sections, the index of the record; undefined for single-object sections.
  recordIndex?: number;
}

const asText = (v: any): string => {
  if (v === null || v === undefined || v === "") {
    return "";
  }
  return String(v);
};

const buildFields = (rows: IChangeFieldRow[]): IChangeFieldRow[] =>
  rows.map((r) => ({ ...r, missing: r.missing ?? asText(r.to) === "" }));

const countMissing = (fields: IChangeFieldRow[]): number =>
  fields.filter((f) => f.missing).length;

// Builds a compact address label from the common address parts.
const addressLabel = (parts: (string | null | undefined)[]): string =>
  parts.filter((p) => asText(p) !== "").join(", ");

export const deriveChangeEntries = (
  values: IHabitationalPolicyChange
): IChangeEntry[] => {
  const entries: IChangeEntry[] = [];

  // --- Others: LOA / LOE / Other request type ---
  // The "Others" board card records a type-level request (LOA / LOE / General
  // Change) that has no dedicated field form. It shows whenever the request
  // `type` has been switched away from the default V2 "PolicyChange".
  const OTHER_REQUEST_TYPE_LABELS: Record<string, string> = {
    LOA: "LOA",
    LOE: "LOE",
    GeneralChange: "Other (General Change)",
    New: "New (upload)",
    "Remarket(upload)": "Remarket (upload)",
  };
  if (values.type && OTHER_REQUEST_TYPE_LABELS[values.type]) {
    const label = OTHER_REQUEST_TYPE_LABELS[values.type];
    entries.push({
      id: "other_request_type",
      section: "otherRequestType",
      subType: values.sub_type || label,
      mode: "REQUEST",
      title: label,
      fields: [],
      missingCount: 0,
    });
  }

  // --- Cancellation / Reinstatement (single object) ---
  // Captured via the "Cancellation / Reinstatement" board card: a Cancel
  // Reason (cancellation_reason) and a Subtype (sub_type: FLAT / SHORT RATE /
  // PRO-RATA). Shows whenever either has been chosen.
  const CANCELLATION_SUBTYPES = ["FLAT", "SHORT RATE", "PRO-RATA"];
  const hasCancellationSubType = CANCELLATION_SUBTYPES.includes(
    asText(values.sub_type)
  );
  if (asText(values.cancellation_reason) !== "" || hasCancellationSubType) {
    const fields = buildFields([
      { label: "Cancel Reason", to: values.cancellation_reason },
      {
        label: "Subtype",
        to: hasCancellationSubType ? values.sub_type : null,
      },
    ]);
    entries.push({
      id: "cancellation_reinstatement",
      section: "cancellationReinstatement",
      subType: "Cancellation/Reinstatement",
      mode: "CHANGE",
      title: "Cancellation / Reinstatement — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Named Insured (single object) ---
  if (values.name_change && asText(values.name_change.new_name) !== "") {
    const fields = buildFields([
      { label: "New name", to: values.name_change.new_name },
      { label: "Date of birth", to: values.name_change.date_of_birth },
      { label: "Relationship", to: values.name_change.relationship },
    ]);
    entries.push({
      id: "name_change",
      section: "nameChange",
      subType: "Name Change",
      mode: "CHANGE",
      title: `Named insured ${values.name_change.new_name || ""} — CHANGE`.trim(),
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Address (single object) ---
  // V2 lets the user record an address change purely via the pill selector
  // (Mailing/Risk location/Both), contact fields, or per-risk-location
  // re-rate rows, without necessarily filling the free-text `address` field
  // (e.g. a risk-location-only re-rate with no mailing address change). So
  // this entry shows whenever ANY of those fields carry data, not just
  // `address`.
  const addressChange = values.address_change;
  const hasRiskLocationUpdates = (
    addressChange?.risk_location_updates || []
  ).some((update) => update.selected);
  const hasAddressChangeData =
    addressChange &&
    [
      addressChange.address,
      addressChange.change_type,
      addressChange.unit_number,
      addressChange.city,
      addressChange.state,
      addressChange.postal_code,
      addressChange.phone,
      addressChange.email,
    ].some((v) => asText(v) !== "");
  if (hasAddressChangeData || hasRiskLocationUpdates) {
    // Each selected risk location gets its own single-line row summarising
    // exactly what changed for it (occupancy type / access type / smokers /
    // number of families), rather than one combined "list of numbers" row —
    // matches how every other field is shown one-per-line.
    const riskLocationFields: IChangeFieldRow[] = (
      addressChange.risk_location_updates || []
    )
      .filter((update) => update.selected)
      .map((update) => {
        const changedParts = [
          update.occupancy_type && `Occupancy type: ${update.occupancy_type}`,
          update.access_type && `Access type: ${update.access_type}`,
          update.smokers && `Smokers: ${update.smokers}`,
          update.no_of_families && `No. of families: ${update.no_of_families}`,
        ].filter(Boolean);
        return {
          label: `Risk location ${update.risk_location_number ?? "--"}`,
          to:
            changedParts.length > 0
              ? changedParts.join(", ")
              : "No re-rate fields changed",
          missing: false,
        };
      });

    const fields = buildFields([
      { label: "Change type", to: addressChange.change_type },
      { label: "Address", to: addressChange.address },
      { label: "Unit", to: addressChange.unit_number, missing: false },
      { label: "City", to: addressChange.city },
      { label: "Province/State", to: addressChange.state },
      { label: "Postal code", to: addressChange.postal_code },
      { label: "Phone", to: addressChange.phone, missing: false },
      { label: "Email", to: addressChange.email, missing: false },
      ...riskLocationFields,
    ]);
    entries.push({
      id: "address_change",
      section: "addressChange",
      subType: "Address Change",
      mode: "CHANGE",
      title: "Address — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Payment Method / Credit Card (single object) ---
  const cc = values.credit_card_info;
  const hasPaymentInfo =
    cc &&
    [
      cc.payment_type,
      cc.cc,
      cc.expiry,
      cc.cvv,
      cc.account_holder_name,
      cc.bank_name,
      cc.account_number,
      cc.institution_number,
      cc.transit_number,
    ].some((v) => asText(v) !== "");
  if (hasPaymentInfo) {
    // Human-readable label for the chosen payment type + authorization.
    const paymentTypeLabel =
      cc.payment_type === "Bank"
        ? "Bank (PAD)"
        : cc.payment_type === "Credit Card"
        ? "Credit card"
        : cc.payment_type || "";
    const authLabel =
      cc.signature_type === "G4Sign"
        ? "Sign via G4 Sign"
        : cc.signature_type === "OnFile"
        ? "On file"
        : null;
    const maskedCard =
      asText(cc.cc) !== "" ? `•••• ${asText(cc.cc).slice(-4)}` : "";

    // Only show the fields relevant to the selected payment type, so the
    // summary matches what the user actually filled in.
    let typeFields: { label: string; to: any; missing?: boolean }[] = [];
    if (cc.payment_type === "Bank") {
      typeFields = [
        { label: "Bank name", to: cc.bank_name },
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Institution number", to: cc.institution_number },
        { label: "Transit number", to: cc.transit_number },
        { label: "Account number", to: cc.account_number },
      ];
    } else if (cc.payment_type === "Credit Card") {
      typeFields = [
        { label: "Card number", to: maskedCard, missing: false },
        { label: "Expiry", to: cc.expiry },
        { label: "Account holder name", to: cc.account_holder_name },
      ];
    } else {
      // Cheque / Other / not yet chosen.
      typeFields = [
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Bank name", to: cc.bank_name },
      ];
    }

    const fields = buildFields([
      { label: "Payment type", to: paymentTypeLabel, missing: false },
      ...typeFields,
      ...(authLabel
        ? [{ label: "Authorization", to: authLabel, missing: false }]
        : []),
    ]);
    entries.push({
      id: "payment_method_change",
      section: "paymentMethodChange",
      subType: "Payment Method Change",
      mode: "CHANGE",
      title: `Payment method${
        paymentTypeLabel ? ` (${paymentTypeLabel})` : ""
      } — CHANGE`,
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Risk Location (record array) ---
  // A "Change" (substitution) replaces an existing risk location (tracked in
  // delete_risk_location) with a newly added record, so those records show as
  // CHANGE with the replaced risk location called out. Plain additions still
  // show as ADD.
  // Each record carries its OWN mode ("Add"/"Change") and, for a Change, its
  // own snapshot of the record being replaced — captured at save time. This
  // means adding a new "Add" record never retroactively flips earlier
  // "Change" records to ADD. Falls back to the section-level `type` only for
  // legacy records saved before per-record mode existed.
  const sectionIsRiskLocationChange =
    values.risk_location_change?.type === "Change";
  (values.risk_location_change?.records || []).forEach((rec: any, index) => {
    const label = addressLabel([rec.address, rec.city, rec.province_or_state]);
    const isSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsRiskLocationChange;
    const mode = isSubstitution ? "CHANGE" : "ADD";
    const replacedRiskLocation =
      rec.replaced_record_details ??
      values.risk_location_change?.delete_risk_location_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedRiskLocation?.incremental_number ??
      values.risk_location_change?.delete_risk_location;
    const fields = buildFields([
      // The risk location being replaced (removed): number + address.
      ...(isSubstitution
        ? [
            {
              label: "Risk location to replace — number",
              to: replacedNumber,
              missing: false,
            },
            {
              label: "Risk location to replace — address",
              to:
                addressLabel([
                  replacedRiskLocation?.address,
                  replacedRiskLocation?.city,
                  replacedRiskLocation?.province_or_state,
                  replacedRiskLocation?.postal_code,
                ]) || null,
              missing: false,
            },
          ]
        : []),
      { label: isSubstitution ? "New risk location — address" : "Address", to: rec.address },
      { label: isSubstitution ? "New risk location — city" : "City", to: rec.city },
      { label: isSubstitution ? "New risk location — province/state" : "Province/State", to: rec.province_or_state },
      { label: isSubstitution ? "New risk location — postal code" : "Postal code", to: rec.postal_code },
    ]);
    entries.push({
      id: `risk_location_change-${index}`,
      section: "riskLocationChange",
      subType: "Risk Location Change",
      mode,
      title: `Risk location ${label || `#${index + 1}`} — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  if (
    !sectionIsRiskLocationChange &&
    asText(values.risk_location_change?.delete_risk_location) !== ""
  ) {
    const fields = buildFields([
      {
        label: "Risk location",
        to: values.risk_location_change.delete_risk_location,
        missing: false,
      },
    ]);
    entries.push({
      id: "risk_location_change-delete",
      section: "riskLocationChange",
      subType: "Risk Location Change",
      mode: "DELETE",
      title: `Risk location ${values.risk_location_change.delete_risk_location} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Coverage (record array) ---
  // A "Change" (substitution) replaces an existing coverage (tracked in
  // delete_coverage) with a newly added record. Plain additions show as ADD.
  // Per-record mode (see Risk Location note above): each record keeps its own
  // "Add"/"Change" mode so a later Add never flips earlier Change rows.
  const sectionIsCoverageChange = values.coverage_change?.type === "Change";
  (values.coverage_change?.records || []).forEach((rec: any, index) => {
    const isCoverageSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsCoverageChange;
    const mode = isCoverageSubstitution ? "CHANGE" : "ADD";
    const replacedCoverage =
      rec.replaced_record_details ??
      values.coverage_change?.delete_coverage_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedCoverage?.incremental_number ??
      values.coverage_change?.delete_coverage;
    const fields = buildFields([
      // The coverage being replaced (removed): number.
      ...(isCoverageSubstitution
        ? [
            {
              label: "Coverage to replace — number",
              to: replacedNumber,
              missing: false,
            },
          ]
        : []),
      { label: isCoverageSubstitution ? "New coverage — number" : "Coverage #", to: rec.incremental_number },
      { label: isCoverageSubstitution ? "New coverage — risk location" : "Risk location", to: rec.coverage_risk_location },
    ]);
    entries.push({
      id: `coverage_change-${index}`,
      section: "coverageChange",
      subType: "Coverage Change",
      mode,
      title: `Coverage ${
        rec.incremental_number ? `#${rec.incremental_number}` : `#${index + 1}`
      } — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  if (
    !sectionIsCoverageChange &&
    asText(values.coverage_change?.delete_coverage) !== ""
  ) {
    const fields = buildFields([
      {
        label: "Coverage",
        to: values.coverage_change.delete_coverage,
        missing: false,
      },
    ]);
    entries.push({
      id: "coverage_change-delete",
      section: "coverageChange",
      subType: "Coverage Change",
      mode: "DELETE",
      title: `Coverage ${values.coverage_change.delete_coverage} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Schedule (record array) ---
  // A "Change" (substitution) replaces an existing schedule item (tracked in
  // delete_schedule) with a newly added record. Plain additions show as ADD.
  // Per-record mode (see Risk Location note above).
  const sectionIsScheduleChange = values.schedule_change?.type === "Change";
  (values.schedule_change?.records || []).forEach((rec: any, index) => {
    const isScheduleSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsScheduleChange;
    const mode = isScheduleSubstitution ? "CHANGE" : "ADD";
    const replacedSchedule =
      rec.replaced_record_details ??
      values.schedule_change?.delete_schedule_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedSchedule?.incremental_number ??
      values.schedule_change?.delete_schedule;
    const fields = buildFields([
      // The schedule item being replaced (removed): number + property number.
      ...(isScheduleSubstitution
        ? [
            {
              label: "Schedule to replace — number",
              to: replacedNumber,
              missing: false,
            },
            {
              label: "Schedule to replace — property number",
              to: replacedSchedule?.property_number ?? null,
              missing: false,
            },
          ]
        : []),
      { label: isScheduleSubstitution ? "New schedule — number" : "Schedule #", to: rec.incremental_number },
      { label: isScheduleSubstitution ? "New schedule — property number" : "Property number", to: rec.property_number },
    ]);
    entries.push({
      id: `schedule_change-${index}`,
      section: "scheduleChange",
      subType: "Schedule Change",
      mode,
      title: `Schedule ${
        rec.property_number
          ? `property ${rec.property_number}`
          : `#${index + 1}`
      } — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  if (
    !sectionIsScheduleChange &&
    asText(values.schedule_change?.delete_schedule) !== ""
  ) {
    const fields = buildFields([
      {
        label: "Schedule",
        to: values.schedule_change.delete_schedule,
        missing: false,
      },
    ]);
    entries.push({
      id: "schedule_change-delete",
      section: "scheduleChange",
      subType: "Schedule Change",
      mode: "DELETE",
      title: `Schedule ${values.schedule_change.delete_schedule} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Interested Party (record array) ---
  // A "Change" (substitution) replaces an existing interested party (tracked
  // in delete_interested_party) with a newly added record. Plain additions
  // show as ADD.
  // Per-record mode (see Risk Location note above).
  const sectionIsInterestedPartyChange =
    values.interested_party_change?.type === "Change";
  (values.interested_party_change?.records || []).forEach(
    (rec: any, index) => {
      const label =
        rec.interested_party_name ||
        rec.mortgage_or_loss_payee_name ||
        addressLabel([
          rec.mortgage_or_loss_payee_address,
          rec.mortgage_or_loss_payee_city,
        ]);
      const isInterestedPartySubstitution = rec.mode
        ? rec.mode === "Change"
        : sectionIsInterestedPartyChange;
      const mode = isInterestedPartySubstitution ? "CHANGE" : "ADD";
      const replacedInterestedParty =
        rec.replaced_record_details ??
        values.interested_party_change?.delete_interested_party_details;
      const replacedNumber =
        rec.replaced_delete_value ??
        replacedInterestedParty?.incremental_number ??
        values.interested_party_change?.delete_interested_party;
      const fields = buildFields([
        // The interested party being replaced (removed): number, risk
        // location, name.
        ...(isInterestedPartySubstitution
          ? [
              {
                label: "Party to replace — number",
                to: replacedNumber,
                missing: false,
              },
              {
                label: "Party to replace — risk location",
                to: replacedInterestedParty?.location_number ?? null,
                missing: false,
              },
              {
                label: "Party to replace — name",
                to:
                  replacedInterestedParty?.mortgage_or_loss_payee_name ?? null,
                missing: false,
              },
            ]
          : []),
        { label: isInterestedPartySubstitution ? "New party — name" : "Name", to: rec.mortgage_or_loss_payee_name },
        {
          label: isInterestedPartySubstitution ? "New party — nature of interest" : "Nature of interest",
          to: rec.mortgage_or_loss_payee_nature_of_interest,
        },
        { label: isInterestedPartySubstitution ? "New party — address" : "Address", to: rec.mortgage_or_loss_payee_address },
      ]);
      entries.push({
        id: `interested_party_change-${index}`,
        section: "interestedPartyChange",
        subType: "Interested Party Change",
        mode,
        title: `Interested party ${label || `#${index + 1}`} — ${mode}`,
        fields,
        missingCount: countMissing(fields),
        recordIndex: index,
      });
    }
  );
  if (
    !sectionIsInterestedPartyChange &&
    asText(values.interested_party_change?.delete_interested_party) !== ""
  ) {
    const fields = buildFields([
      {
        label: "Interested party",
        to: values.interested_party_change.delete_interested_party,
        missing: false,
      },
    ]);
    entries.push({
      id: "interested_party_change-delete",
      section: "interestedPartyChange",
      subType: "Interested Party Change",
      mode: "DELETE",
      title: `Interested party ${values.interested_party_change.delete_interested_party} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Location Assignment (record array) ---
  // Each record carries its own mode ("Add"/"Change") and, for Change, its
  // own substitution target — captured at save time — so relabeling one
  // record (e.g. clicking "Change" for a new record) never retroactively
  // changes earlier records' displayed mode.
  (values.location_assignment_change?.records || []).forEach(
    (rec: any, index: number) => {
      const isSubstitution = rec.mode === "Change";
      const mode = isSubstitution ? "CHANGE" : "ADD";
      const label = [rec.risk_location_number, rec.interested_party_number]
        .filter((v) => v !== null && v !== undefined && v !== "")
        .join(" / ");
      const fields = buildFields([
        ...(isSubstitution
          ? [
              {
                label: "Assignment replaced",
                to: rec.delete_location_assignment,
                missing: false,
              },
            ]
          : []),
        { label: "Risk location", to: rec.risk_location_number },
        { label: "Interested party", to: rec.interested_party_number },
        { label: "Nature of interest", to: rec.nature_of_interest, missing: false },
        { label: "Notes", to: rec.notes, missing: false },
      ]);
      entries.push({
        id: `location_assignment_change-${index}`,
        section: "locationAssignmentChange",
        subType: "Location Assignment Change",
        mode,
        title: `Location Assignment ${label || `#${index + 1}`} — ${mode}`,
        fields,
        missingCount: countMissing(fields),
        recordIndex: index,
      });
    }
  );

  return entries;
};
